import { createContext, useContext, useState, useCallback, useRef } from 'react'

// ─────────────────────────────────────────────────────────────
// Toast types
// ─────────────────────────────────────────────────────────────
type ToastType = 'success' | 'error' | 'warning' | 'info'

interface Toast {
  id: string
  message: string
  type: ToastType
}

interface ToastContextValue {
  success: (message: string) => void
  error: (message: string) => void
  warning: (message: string) => void
  info: (message: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const TOAST_DURATION_MS = 3500
const MAX_TOASTS = 5

// ─────────────────────────────────────────────────────────────
// Toast icons
// ─────────────────────────────────────────────────────────────
const ICONS: Record<ToastType, string> = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ',
}

const COLORS: Record<ToastType, { bg: string; border: string; icon: string }> = {
  success: {
    bg: 'rgba(34,197,94,0.1)',
    border: 'rgba(34,197,94,0.3)',
    icon: 'var(--success)',
  },
  error: {
    bg: 'rgba(239,68,68,0.1)',
    border: 'rgba(239,68,68,0.3)',
    icon: 'var(--error)',
  },
  warning: {
    bg: 'rgba(234,179,8,0.1)',
    border: 'rgba(234,179,8,0.3)',
    icon: 'var(--warning)',
  },
  info: {
    bg: 'rgba(59,130,246,0.1)',
    border: 'rgba(59,130,246,0.3)',
    icon: 'var(--info)',
  },
}

// ─────────────────────────────────────────────────────────────
// ToastProvider
// ─────────────────────────────────────────────────────────────
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
    const timer = timersRef.current.get(id)
    if (timer) { clearTimeout(timer); timersRef.current.delete(id) }
  }, [])

  const show = useCallback((message: string, type: ToastType) => {
    const id = `toast-${Date.now()}-${Math.random()}`
    setToasts((prev) => {
      const next = [...prev, { id, message, type }]
      return next.slice(-MAX_TOASTS) // keep max 5
    })
    const timer = setTimeout(() => dismiss(id), TOAST_DURATION_MS)
    timersRef.current.set(id, timer)
  }, [dismiss])

  const value: ToastContextValue = {
    success: (msg) => show(msg, 'success'),
    error:   (msg) => show(msg, 'error'),
    warning: (msg) => show(msg, 'warning'),
    info:    (msg) => show(msg, 'info'),
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Toast container — fixed bottom-right desktop, bottom-center mobile */}
      <div style={{
        position: 'fixed',
        top: '4.5rem',
        right: '1.5rem',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        pointerEvents: 'none',
        maxWidth: '360px',
        width: 'calc(100vw - 2rem)',
      }}>
        {toasts.map((toast) => {
          const colors = COLORS[toast.type]
          return (
            <div
              key={toast.id}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.6rem',
                background: 'var(--bg-card)',
                border: `1px solid ${colors.border}`,
                borderLeft: `3px solid ${colors.icon}`,
                borderRadius: '8px',
                padding: '0.65rem 0.875rem',
                boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
                pointerEvents: 'all',
                animation: 'toastIn 0.2s ease-out',
                position: 'relative',
                overflow: 'hidden',
              }}
              onClick={() => dismiss(toast.id)}
            >
              {/* Tinted background strip — opaque card + subtle color hint */}
              <div style={{
                position: 'absolute', inset: 0,
                background: colors.bg,
                opacity: 0.35,
                pointerEvents: 'none',
              }} />
              <span style={{
                color: colors.icon,
                fontWeight: 700,
                fontSize: '0.8rem',
                flexShrink: 0,
                marginTop: '0.1rem',
                position: 'relative',
              }}>
                {ICONS[toast.type]}
              </span>
              <span style={{
                fontSize: '0.855rem',
                color: 'var(--text-primary)',
                lineHeight: 1.5,
                flex: 1,
                minWidth: 0,
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                position: 'relative',
              }}>
                {toast.message}
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); dismiss(toast.id) }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  lineHeight: 1,
                  padding: '0.1rem',
                  flexShrink: 0,
                  marginTop: '0.1rem',
                  marginLeft: '0.25rem',
                  position: 'relative',
                }}
              >
                ✕
              </button>
            </div>
          )
        })}
      </div>
      <style>{`
      @keyframes toastIn {
      from { opacity: 0; transform: translateY(-8px) scale(0.97); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
      }
      `}</style>
    </ToastContext.Provider>
  )
}

// ─────────────────────────────────────────────────────────────
// useToast hook
// ─────────────────────────────────────────────────────────────
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}
